import style from './IntegrationCRM.module.css';
import React, { useState, useRef, FormEvent } from 'react';
import { NewRequestDemoForm } from '@/components/NewRequestDemoForm/NewRequestDemoForm';
import { clsx } from 'clsx';
import { Container } from '@/components/Containers/Container';
import { Typography } from '@/components/Typography/Typography';
import { COLORS } from '@/constants/colors';
import { PATH_SUCCESS } from '@/constants/spa-routes';
import { useRouter } from 'next/router';

type IntegrationCRMProps = {
  isMobile: boolean;
};

const wait = async (ms: number) => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

export const IntegrationCRM = ({ isMobile }: IntegrationCRMProps) => {
  const [isSuccess, setIsSuccess] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const containerRef = useRef<React.RefObject<HTMLDivElement> | undefined>();
  const { push } = useRouter();
  const onButtonClick = async (event: FormEvent) => {
    if (!isExpanded) {
      event.preventDefault();
      setIsExpanded(true);
      //@ts-ignore
      containerRef.current.style.height =
        //@ts-ignore
        containerRef.current.firstChild.clientHeight + 'px';

      await wait(1500);
      //@ts-ignore
      containerRef.current.style.overflow = 'visible';
    }
  };

  const onFormSubmit = async (response: any) => {
    if (response.success) {
      setIsSuccess(true);
      //@ts-ignore
      window.scrollTo(0, containerRef.current.offsetParent.offsetTop);
      await wait(300);
      setIsExpanded(false);
      //@ts-ignore
      containerRef.current.style.overflow = 'hidden';
      //@ts-ignore
      containerRef.current.style.height = 0;

      const name = response.data?.name;
      const email = response.data?.email;
      const url = `${PATH_SUCCESS.ROOT}?name=${encodeURI(name || '')}&email=${encodeURI(email || '')}`;

      await push(url);
    }
  };

  return (
    <section
      className={clsx(
        'relative bg-[radial-gradient(41.81%_50%_at_50%_50%,#132B4E_0%,#0C141B_100%)]'
      )}
    >
      <Container className="p-0">
        <div
          className={`${isMobile ? style.containerMobile : style.containerDesktop} ${isExpanded ? style.expanded : ''}`}
        >
          <picture className="md:mb-10 w-full h-full object-contain md:max-w-[33.125vw] max-w-[80%]">
            <source
              srcSet="/img/freshdesk-integration/integration-images.webp"
              media="(min-width: 640px)"
            />
            <img
              className="h-full w-full object-contain object-bottom"
              src="/img/freshdesk-integration/integration-images-mobile.webp"
              alt="integration images"
            />
          </picture>
          <div className={style.title}>
            <Typography as="h2" $type="Heading1" $color={COLORS.WHITE} className="font-formula">
              Seamless integration with your current CRM, even if it's not Freshdesk
            </Typography>
            <Typography $type="Heading4" $color={COLORS.WHITE} className="font-jamjuree opacity-60">
              Feel free to mention it, and we will send you a demo showcasing how CoSupport AgentTM
              can be integrated with your existing CRM
            </Typography>
            {
              <p className={`${style.successMessage} ${isSuccess ? style.visible : ''}`}>
                <span className={'green'}>Great, you're set!</span>
                <br />
                Thanks for opting to explore our demo – look out for our follow-up. Please check
                your email! <span className={'green'}>→</span>
              </p>
            }
          </div>
          <div
            //@ts-ignore
            ref={containerRef}
            style={{ height: 0 }}
            className={`${style.formContainer} ${isSuccess ? style.hidden : ''}`}
          >
            <div>
              <div className={style.formText}>
                {isMobile ? (
                  <p>
                    For a tailored experience, please fill out this form. It'll help us customize
                    your demo preview.
                  </p>
                ) : (
                  <p>
                    For a tailored experience, please fill out this form.
                    <br /> It'll help us customize your demo preview.
                  </p>
                )}
              </div>
              <NewRequestDemoForm
                tags={['Freshdesk Page Form - Other']}
                id="requestDemoForm"
                name={'required'}
                email={{
                  required: true,
                  validationPopupPosition: isMobile ? 'bottom' : 'right',
                }}
                phone
                tickets={{ required: true }}
                darkMode={true}
                onSubmit={onFormSubmit}
                showSpinner={true}
              />
            </div>
          </div>
          {!isSuccess && (
            <button onClick={onButtonClick} className={style.button} form={'requestDemoForm'}>
              <span className={style.gradient}></span>
              Let's Talk
            </button>
          )}
        </div>
      </Container>
    </section>
  );
};
